import React, { useContext, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, Bell, HelpCircle, Crown, Wallet } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ChatPopupContext } from '@/components/messaging/FacebookStyleChat/ChatPopupManager';
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown';
import { useNotificationCount } from '@/hooks/use-notifications';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useSubscription } from '@/hooks/use-subscription';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useClientWallet } from '@/hooks/use-client-wallet';
import { WalletDialog } from '@/components/wallet/WalletDialog';
import { FilmmakerWalletButton } from '@/components/wallet/FilmmakerWalletDialog';
import { UnifiedWalletDropdown } from '@/components/wallet/UnifiedWalletDropdown';
import { useAuth } from '@/contexts/AuthContext';

interface GlobalHeaderProps {
  className?: string;
  dashboardType?: 'filmmaker' | 'client' | 'admin';
}

export const GlobalHeader: React.FC<GlobalHeaderProps> = ({ className, dashboardType }) => {
  const { userType } = useAuth();
  const isDual = userType === 'both';
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const [isWalletOpen, setIsWalletOpen] = useState(false);

  // Wallet — only visible for users with a client account
  const { data: walletData } = useClientWallet();
  const hasClientAccount = !!walletData?.client_id;
  const walletBalance = walletData?.wallet ?? 0;

  // Try to use the context, but don't throw if it's not available
  const chatContext = useContext(ChatPopupContext);
  const toggleChatList = chatContext?.toggleChatList || (() => {});
  const totalUnreadCount = chatContext?.totalUnreadCount || 0;

  // Get notification unread count
  const notificationUnreadCount = useNotificationCount();

  // Get user subscription tier
  const { isPro } = useSubscription(userId || undefined);

  // Check if user is logged in using Supabase auth
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsUserLoggedIn(!!session);
      setUserId(session?.user?.id || null);
    };

    checkAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsUserLoggedIn(!!session);
      setUserId(session?.user?.id || null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Function to open admin support chat
  const openSupportChat = async () => {
    if (!chatContext) return;

    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        console.error('No user logged in');
        return;
      }

      // Get current user's entity ID based on their role
      let currentEntityId: string | null = null;
      let currentRole: 'filmmaker' | 'client' = 'filmmaker';

      // Check if user is filmmaker
      const { data: filmmaker } = await supabase
        .from('filmmaker')
        .select('filmmaker_id')
        .eq('user_id', currentUser.id)
        .single();

      if (filmmaker) {
        currentEntityId = filmmaker.filmmaker_id;
        currentRole = 'filmmaker';
      } else {
        // Check if user is client
        const { data: client } = await supabase
          .from('client')
          .select('client_id')
          .eq('user_id', currentUser.id)
          .single();

        if (client) {
          currentEntityId = client.client_id;
          currentRole = 'client';
        }
      }

      if (!currentEntityId) {
        console.error('Could not find entity ID for current user');
        return;
      }

      // Admin setup matching the existing admin messaging system
      const ADMIN_CLIENT_ID = '7c5377d6-727d-4c6e-bbcb-4b4c34009098';
      const ADMIN_NAME = 'Find My Filmmaker Admin';
      const ADMIN_LOGO = 'https://vftrlkabvhbpieuoqhzs.supabase.co/storage/v1/object/public/logo/fmf-mobile.png';

      // Create proper room name based on user role
      let roomName: string;
      if (currentRole === 'filmmaker') {
        roomName = `chat_client_${ADMIN_CLIENT_ID}_filmmaker_${currentEntityId}`;
      } else {
        roomName = `chat_client_${currentEntityId}_filmmaker_${ADMIN_CLIENT_ID}`;
      }

      // Open chat with admin using proper room name format
      chatContext.openChat({
        id: roomName,
        name: ADMIN_NAME,
        avatar: ADMIN_LOGO,
        role: 'client' as any,
        isMinimized: false,
        unreadCount: 0
      });
    } catch (error) {
      console.error('Error opening support chat:', error);
    }
  };

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isNotificationOpen && notificationButtonRef.current) {
      const rect = notificationButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isNotificationOpen]);

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        isNotificationOpen &&
        !target.closest('.notification-dropdown') &&
        !target.closest('.notification-toggle-button')
      ) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isNotificationOpen]);

  // Don't render on mobile - mobile uses different header system
  if (isMobile) {
    return null;
  }

  return (
    <div className={cn(
      "h-14 shrink-0 flex items-center justify-end gap-3 px-6 border-b border-border bg-background/80 backdrop-blur-sm",
      className
    )}>
      {/* Spacer to push actions to the right */}
      <div className="flex-1" />

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        {/* Dual users: single unified dropdown (Wallet + Earnings choices) */}
        {isUserLoggedIn && isDual && <UnifiedWalletDropdown />}

        {/* Filmmaker-only: standalone earnings button */}
        {isUserLoggedIn && !isDual && dashboardType === 'filmmaker' && <FilmmakerWalletButton />}

        {/* Client-only: standalone wallet button */}
        {isUserLoggedIn && !isDual && dashboardType === 'client' && hasClientAccount && (
          <button
            onClick={() => setIsWalletOpen(true)}
            className="flex items-center gap-1.5 h-9 px-3 rounded-full hover:bg-muted transition-colors group text-sm font-medium text-muted-foreground hover:text-foreground"
            title="Wallet"
          >
            <Wallet className="h-4 w-4" />
            <span>£{walletBalance.toLocaleString()}</span>
          </button>
        )}

        {/* Support Chat Button (only when logged in) */}
        {isUserLoggedIn && chatContext && (
          <button
            onClick={openSupportChat}
            className="relative h-9 w-9 flex items-center justify-center rounded-full hover:bg-muted transition-colors group"
            title={isPro ? "Priority Support - Pro Member" : "Chat with Support"}
          >
            <HelpCircle className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
            {isPro && (
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
                className="absolute -top-0.5 -right-0.5"
              >
                <div className="w-4 h-4 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow">
                  <Crown className="h-2.5 w-2.5 text-white" />
                </div>
              </motion.div>
            )}
          </button>
        )}

        {/* Chat Button */}
        {chatContext && (
          <button
            onClick={toggleChatList}
            className="chat-toggle-button relative h-9 w-9 flex items-center justify-center rounded-full hover:bg-muted transition-colors group"
            title="Messages"
          >
            <MessageCircle className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
            <AnimatePresence>
              {totalUnreadCount > 0 && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 25,
                    duration: 0.2
                  }}
                  className="absolute -top-0.5 -right-0.5"
                >
                  <Badge
                    variant="destructive"
                    className="h-4 min-w-[16px] p-0 flex items-center justify-center text-[10px]"
                  >
                    {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                  </Badge>
                </motion.div>
              )}
            </AnimatePresence>
          </button>
        )}

        {/* Notification Button */}
        <button
          ref={notificationButtonRef}
          onClick={() => setIsNotificationOpen(!isNotificationOpen)}
          className="notification-toggle-button relative h-9 w-9 flex items-center justify-center rounded-full hover:bg-muted transition-colors group"
          title="Notifications"
        >
          <Bell className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
          <AnimatePresence>
            {notificationUnreadCount > 0 && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 500,
                  damping: 25,
                  duration: 0.2
                }}
                className="absolute -top-0.5 -right-0.5"
              >
                <Badge
                  variant="destructive"
                  className="h-4 min-w-[16px] p-0 flex items-center justify-center text-[10px]"
                >
                  {notificationUnreadCount > 99 ? '99+' : notificationUnreadCount}
                </Badge>
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </div>

      {/* Notification Dropdown - Portal to escape overflow:hidden */}
      {/* Wallet Dialog */}
      <WalletDialog
        open={isWalletOpen}
        onOpenChange={setIsWalletOpen}
        clientId={walletData?.client_id}
        walletBalance={walletBalance}
      />

      {createPortal(
        <AnimatePresence>
          {isNotificationOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 300
              }}
              style={{
                position: 'fixed',
                top: dropdownPosition.top,
                right: dropdownPosition.right,
              }}
              className="notification-dropdown w-[400px] h-[500px] bg-background border border-border rounded-lg shadow-xl z-[9999] overflow-hidden"
            >
              <NotificationDropdown
                isOpen={isNotificationOpen}
                onClose={() => setIsNotificationOpen(false)}
              />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default GlobalHeader;
